import { useEffect, useState, useCallback, useRef } from "react";
import { Download, X } from "lucide-react";
import { APP_NAME } from "@/lib/brand";

interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISS_KEY = "ironclad-ops-pwa-install-dismissed";

/** PWA install nudge.
 *
 *  Lives in the top-right corner (out of the way of bottom save / action
 *  bars on forms and side drawers, which was the previous footgun). Starts
 *  expanded for ~6 s so the user actually sees the offer, then collapses
 *  to a small download icon they can re-open. Explicit dismiss persists
 *  forever via localStorage. */
export function InstallPrompt() {
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [visible, setVisible] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const collapseTimer = useRef<number | null>(null);

    useEffect(() => {
        if (localStorage.getItem(DISMISS_KEY) === "1") return;

        const handler = (e: Event) => {
            e.preventDefault();
            setDeferredPrompt(e as BeforeInstallPromptEvent);
            setVisible(true);
            setCollapsed(false);
            if (collapseTimer.current) window.clearTimeout(collapseTimer.current);
            collapseTimer.current = window.setTimeout(() => setCollapsed(true), 6000);
        };
        window.addEventListener("beforeinstallprompt", handler);
        return () => {
            window.removeEventListener("beforeinstallprompt", handler);
            if (collapseTimer.current) window.clearTimeout(collapseTimer.current);
        };
    }, []);

    const handleInstall = useCallback(async () => {
        if (!deferredPrompt) return;
        await deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === "accepted") {
            setVisible(false);
        }
        setDeferredPrompt(null);
    }, [deferredPrompt]);

    const handleDismiss = useCallback(() => {
        localStorage.setItem(DISMISS_KEY, "1");
        setVisible(false);
        setDeferredPrompt(null);
        if (collapseTimer.current) window.clearTimeout(collapseTimer.current);
    }, []);

    if (!visible) return null;

    if (collapsed) {
        return (
            <button
                type="button"
                onClick={() => setCollapsed(false)}
                aria-label={`Install ${APP_NAME}`}
                title={`Install ${APP_NAME}`}
                className="fixed top-3 right-3 z-[9998] inline-flex h-8 w-8 items-center justify-center rounded-full border border-border bg-card text-foreground/80 shadow-sm hover:text-foreground"
            >
                <Download className="h-4 w-4" />
            </button>
        );
    }

    return (
        <div
            role="complementary"
            aria-label="Install app"
            className="fixed top-3 right-3 z-[9998] flex max-w-xs items-center gap-2 rounded-full border border-border bg-card py-1.5 pl-3 pr-1.5 shadow-md"
        >
            <Download className="h-3.5 w-3.5 shrink-0 text-foreground/80" />
            <span className="truncate text-xs text-card-foreground">
                Install <strong className="font-semibold">{APP_NAME}</strong>
            </span>
            <button
                type="button"
                onClick={handleInstall}
                className="rounded-full bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground hover:bg-primary/90"
            >
                Install
            </button>
            <button
                type="button"
                onClick={handleDismiss}
                aria-label="Dismiss install prompt"
                title="Don't show again"
                className="inline-flex h-6 w-6 items-center justify-center rounded-full text-foreground/80 hover:text-foreground"
            >
                <X className="h-3.5 w-3.5" />
            </button>
        </div>
    );
}
